import { NavigationStart, Router } from '@angular/router';
import { AfterViewInit, Component, inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ResumenExpedienteDto } from '../../../dto/expediente/resumen-expediente.dto';
import { ResumenGeneralService } from '../../../services/resumen-general.service';
import { AlertaModalService } from '../../../services/util/alerta-modal.service';
import { EventoService } from '../../../services/evento.service';
import { EventoDto } from '../../../dto/expediente/evento.dto';
import { Constants } from '../../../constants/constants';
import { PoliticaTratamientoDatosPersonalesComponent } from '../../util/modal-politica/politica-tratamiento-datos-personales/politica-tratamiento-datos-personales.component';
import { ComunicadoDto } from '../../../dto/comunicado/comunicado.dto';
import { PowerbiService } from '../../../services/powerbi.service';
import { DAXQueryDto } from '../../../dto/powerbi/dax.query.dto';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { JustinoControlAccesoService } from '../../../services/justino-control-acceso.service';
import { TableColumn } from '../../../dto/util/table-column';

@Component({
  selector: 'app-resumen-general',
  templateUrl: './resumen-general.component.html',
  styleUrl: './resumen-general.component.scss',
})
export class ResumenGeneralComponent implements OnInit, AfterViewInit {
  private readonly resumenExpedienteService = inject(ResumenGeneralService);
  private readonly alertaService = inject(AlertaModalService);
  private readonly eventoService = inject(EventoService);

  @ViewChild(PoliticaTratamientoDatosPersonalesComponent)
  politicaTratamientoDatosPersonalesComponent!: PoliticaTratamientoDatosPersonalesComponent;

  @ViewChild('barChartCanvas', { static: false })
  barChartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('pieChartCanvas', { static: false })
  pieChartCanvas!: ElementRef<HTMLCanvasElement>;

  procesos: ResumenExpedienteDto[] = [];
  procesosMostrados: any[] = [];
  terminoBusqueda: string = '';
  eventos: EventoDto[] = [];
  comunicados: ComunicadoDto[] = [];
  tipoUsuario: string = '';
  esAbogado: boolean = false;
  esCiudadano: boolean = false;
  graficas: any = {};
  graficasBar: any = {};
  graficasCir: any = {};
  barChart: Chart | null = null;
  pieChart: Chart | null = null;
  fechaInicioSesion: string | null = null;
  columnas: TableColumn[] = [];

  constructor(
    private router: Router,
    private pbiService: PowerbiService,
    private justinoControlAccesoService: JustinoControlAccesoService
  ) {
    Chart.register(...registerables);
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (event.url.includes('resumen')) {
          this.obtenerRolUsuario();
        }
      }
    });
  }

  ngOnInit(): void {
    this.obtenerRolUsuario();
    this.cargarProcesos();
    this.cargarDatosSegunRol();
    this.fechaInicioSesion = this.obtenerUltimaHoraFechaLogin();
    this.initColumns();
  }

  ngAfterViewInit(): void {
    // Obtener la información de inicio de sesión
    const infoInicioSesion = JSON.parse(
      sessionStorage.getItem(Constants.USER_INFORMATION_STORAGE) ?? '{}'
    );
    // Verificar si el usuario es ciudadano o abogado y si no ha aceptado la política de datos
    if (
      (this.esCiudadano || this.esAbogado) &&
      infoInicioSesion.aceptoDatos == false
    ) {
      this.politicaTratamientoDatosPersonalesComponent.abrirModal();
    }
  }

  initColumns(): void {
    this.columnas = [
      { title: 'Tipo Proceso', field: 'Tipo_Proceso' },
      { title: 'Número Proceso', field: 'Id' },
      {
        title: 'Días calendarios transcurridos desde el envio de la solicitud',
        field: 'DiasTranscurridos',
      },
    ];
  }

  obtenerUltimaHoraFechaLogin(): string | null {
    const loginTimestamp = localStorage.getItem(Constants.LOGIN_TIMESTAMP);

    if (!loginTimestamp) {
      return null;
    }

    const date = new Date(loginTimestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = String(hours).padStart(2, '0');

    return `${day}/${month}/${year} ${hoursStr}:${minutes} ${ampm}`;
  }

  obtenerRolUsuario(): void {
    const rol = JSON.parse(sessionStorage.getItem(Constants.USER_TYPE) ?? '{}');
    this.tipoUsuario = rol.Nombre || '';
    this.esAbogado = this.tipoUsuario === Constants.ROL_ABOGADO;
    this.esCiudadano = this.tipoUsuario === Constants.ROL_CIUDADANO;
  }

  cargarDatosSegunRol(): void {
    let query = null;
    let dto: DAXQueryDto;
    const infoInicioSesion = JSON.parse(
      sessionStorage.getItem(Constants.USER_INFORMATION_STORAGE) ?? '{}'
    );
    const idUsuario = String(infoInicioSesion.idUsuario);
    if (this.esCiudadano) {
      this.cargarEventos();
      // Carga los datos de indicadores para el ciudadano
      query = `EVALUATE FILTER('star_schema OBJ_Star_Exa_Res','star_schema OBJ_Star_Exa_Res'[Id Usuario] = ${idUsuario})`;
      dto = {
        datasetId: 'portal',
        query: query,
      };
      this.pbiService.getViewBag(dto).subscribe(response => {
        this.graficas = {
          revisados: response[0]['star_schema OBJ_Star_Exa_Res[Revisados]'],
          sinRevisar: response[0]['star_schema OBJ_Star_Exa_Res[Sin Revisar]'],
          totalExpedientes:
            response[0]['star_schema OBJ_Star_Exa_Res[Total Expedientes]'],
        };
      });
    } else if (this.esAbogado) {
      this.cargarComunicados();
      // Carga los datos de indicadores para el abogado
      query = `EVALUATE FILTER('star_schema OBJ_Star_Exa_Rab','star_schema OBJ_Star_Exa_Rab'[Id Usuario] = ${idUsuario})`;
      dto = {
        datasetId: 'portal',
        query: query,
      };
      this.pbiService.getViewBag(dto).subscribe(response => {
        this.graficas = {
          activos: response[0]['star_schema OBJ_Star_Exa_Rab[Activos]'],
          cerrados: response[0]['star_schema OBJ_Star_Exa_Rab[Cerrados]'],
          nuevos: response[0]['star_schema OBJ_Star_Exa_Rab[Nuevos]'],
        };
      });
      // Cargar los datos para las gráficas de barras
      query = `EVALUATE FILTER('star_schema OBJ_Star_Exa_Bar','star_schema OBJ_Star_Exa_Bar'[Id Usuario] = ${idUsuario})`;
      dto = {
        datasetId: 'portal',
        query: query,
      };
      this.pbiService.getViewBag(dto).subscribe(response => {
        this.graficasBar = {
          dimar: response[0]['star_schema OBJ_Star_Exa_Bar[DIMAR]'],
          dnda: response[0]['star_schema OBJ_Star_Exa_Bar[DNDA]'],
          sic: response[0]['star_schema OBJ_Star_Exa_Bar[SIC]'],
          sns: response[0]['star_schema OBJ_Star_Exa_Bar[SNS]'],
          ica: response[0]['star_schema OBJ_Star_Exa_Bar[ICA]'],
          sfc: response[0]['star_schema OBJ_Star_Exa_Bar[SFC]'],
          supersociedades:
            response[0]['star_schema OBJ_Star_Exa_Bar[Supersociedades]'],
        };
        setTimeout(() => this.crearGraficaBarras(), 100);
      });
      // Cargar los datos para las gráficas circulares
      query = `EVALUATE FILTER('star_schema OBJ_Star_Exa_Cir','star_schema OBJ_Star_Exa_Cir'[Id Usuario] = ${idUsuario})`;
      dto = {
        datasetId: 'portal',
        query: query,
      };
      this.pbiService.getViewBag(dto).subscribe(response => {
        this.graficasCir = {
          modificados: response[0]['star_schema OBJ_Star_Exa_Cir[Modificados]'],
          modificadosPor:
            response[0]['star_schema OBJ_Star_Exa_Cir[ModificadosPor]'],
          noModificados:
            response[0]['star_schema OBJ_Star_Exa_Cir[NoModificados]'],
          noModificadosPor:
            response[0]['star_schema OBJ_Star_Exa_Cir[NoModificadosPor]'],
        };
        setTimeout(() => this.crearGraficaTorta(), 100);
      });
    }
  }

  cargarProcesos(): void {
    this.resumenExpedienteService
      .obtenerResumenExpedientesPorUsuario()
      .subscribe({
        next: (response: ResumenExpedienteDto[]) => {
          this.procesos = response;
          this.procesosMostrados = this.mapearProcesosParaVista(
            this.procesos.slice(0, 3)
          );
          console.log(this.procesosMostrados);
        },
        error: err => {
          this.alertaService.error(
            `Error al cargar el resumen de expedientes: ${err.statusText}`
          );
        },
      });
  }

  cargarEventos(): void {
    this.eventoService.obtenerEventosPendientes().subscribe({
      next: eventos => {
        this.eventos = eventos;
      },
      error: err => {
        this.alertaService.error(
          `Error al cargar los eventos: ${err.statusText}`
        );
      },
    });
  }

  cargarComunicados(): void {
    this.resumenExpedienteService.obtenerComunicadosUltimoMes().subscribe({
      next: comunicados => {
        this.comunicados = comunicados;
      },
      error: err => {
        this.alertaService.error(
          `Error al cargar los comunicados: ${err.statusText}`
        );
      },
    });
  }

  mapearProcesosParaVista(expedientes: ResumenExpedienteDto[]): any[] {
    return expedientes.map(expediente => ({
      ...expediente,
      Tipo_Proceso: expediente.Tipo_Proceso || 'No especificado',
      fechaCreacionFormateada: this.formatearFecha(expediente.Fecha_Creacion),
      etapaActualIndex: expediente.Etapa_Proceso.Orden,
      mostrarDetalles: false,
      etapasOrdenadas: expediente.Etapas_Entidad.sort(
        (a, b) => a.Orden - b.Orden
      ),
      DiasTranscurridos: `${Math.floor(
        (new Date().getTime() - new Date(expediente.Fecha_Creacion).getTime()) /
          (1000 * 60 * 60 * 24)
      ).toString()} días`,
    }));
  }

  filtrarProcesos(): void {
    if (this.terminoBusqueda) {
      const procesosFiltrados = this.procesos.filter(proceso =>
        proceso.Radicado.includes(this.terminoBusqueda)
      );
      this.procesosMostrados = this.mapearProcesosParaVista(procesosFiltrados);
    } else {
      this.procesosMostrados = this.mapearProcesosParaVista(
        this.procesos.slice(0, 3)
      );
    }
  }

  formatearFecha(fecha: string): string {
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  toggleDetails(proceso: any): void {
    proceso.mostrarDetalles = !proceso.mostrarDetalles;
  }

  getBadgeClass(etapa: string): string {
    switch (etapa?.toUpperCase()) {
      case 'INICIAL':
        return 'bg-primary';
      case 'NOTIFICACION':
        return 'bg-info';
      case 'CONTRADICCION':
        return 'bg-warning';
      case 'PROBATORIA':
        return 'bg-secondary';
      case 'DECISION':
        return 'bg-success';
      case 'RECURSO':
        return 'bg-danger';
      default:
        return 'bg-light';
    }
  }

  formatearFechaEvento(fechaHora: string): string {
    const date = new Date(fechaHora);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  formatearHoraEvento(fechaHora: string): string {
    const date = new Date(fechaHora);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  navegarA(ruta: string): void {
    this.router.navigate([ruta]);
  }

  crearGraficaBarras(): void {
    if (!this.barChartCanvas || !this.graficasBar) {
      return;
    }

    if (this.barChart) {
      this.barChart.destroy();
    }

    const ctx = this.barChartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      return;
    }

    const labels = [
      'Supersociedades',
      'Supersalud',
      'Superfinanciera',
      'DNDA',
      'DIMAR',
      'ICA',
      'SIC',
    ];
    const data = [
      this.graficasBar.supersociedades || 0,
      this.graficasBar.sns || 0,
      this.graficasBar.sfc || 0,
      this.graficasBar.dnda || 0,
      this.graficasBar.dimar || 0,
      this.graficasBar.ica || 0,
      this.graficasBar.sic || 0,
    ];

    const config: ChartConfiguration = {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Expedientes activos',
            data: data,
            backgroundColor: 'rgba (9, 71,128, 1)',
            borderColor: 'rgba(9, 71,128, 1.2)',
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Expedientes activos por entidad',
            font: {
              size: 16,
              weight: 'bold',
            },
          },
          legend: {
            display: false,
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 2,
            },
          },
        },
      },
    };

    this.barChart = new Chart(ctx, config);
  }

  crearGraficaTorta(): void {
    if (!this.pieChartCanvas || !this.graficasCir) {
      return;
    }

    if (this.pieChart) {
      this.pieChart.destroy();
    }

    const ctx = this.pieChartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      return;
    }

    const modificados = this.graficasCir.modificados || 0;
    const noModificados = this.graficasCir.noModificados || 0;
    const total = modificados + noModificados;

    const config: ChartConfiguration = {
      type: 'pie',
      data: {
        labels: ['Expedientes con cambio', 'Expedientes sin cambio'],
        datasets: [
          {
            data: [modificados, noModificados],
            backgroundColor: ['rgba(9, 71, 128, 1)', 'rgba(247, 175, 66, 1)'],
            borderColor: ['rgba(9, 71, 128, 0.8)', 'rgba(247, 175, 66, 0.8)'],
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Actividad reciente - Cambios de los procesos',
            font: {
              size: 16,
              weight: 'bold',
            },
            padding: {
              bottom: 20,
            },
          },
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              generateLabels: chart => {
                const data = chart.data;
                if (data.labels && data.datasets && data.datasets[0]) {
                  const dataset = data.datasets[0];
                  const bgColors = Array.isArray(dataset.backgroundColor)
                    ? dataset.backgroundColor
                    : [];
                  const borderColors = Array.isArray(dataset.borderColor)
                    ? dataset.borderColor
                    : [];
                  return data.labels.map((label, i) => {
                    const value = (dataset.data[i] as number) || 0;
                    const percentage =
                      total > 0 ? ((value / total) * 100).toFixed(0) : 0;
                    const bgColor =
                      (bgColors[i] as string) || 'rgba(128, 128, 128, 0.8)';
                    const borderColor =
                      (borderColors[i] as string) || 'rgba(128, 128, 128, 1)';
                    return {
                      text: `${label}: ${value} procesos (${percentage}%)`,
                      fillStyle: bgColor,
                      strokeStyle: borderColor,
                      lineWidth: (dataset.borderWidth as number) || 1,
                      hidden: false,
                      index: i,
                    };
                  });
                }
                return [];
              },
            },
          },
        },
      },
    };

    this.pieChart = new Chart(ctx, config);
  }
}
